import { useCallback, Dispatch, SetStateAction } from 'react';
import { GameState, Player } from '../../../domain/entities';
import { getPlayerTeam, addNewsItem } from '../../../services/GameService';
import { MAX_SQUAD_SIZE, YOUTH_ACADEMY_LEVELS, getPlayerWeeklyWage } from '../../../domain/constants';

type SetGameState = Dispatch<SetStateAction<GameState | { gameStarted: false; }>>;

interface UseYouthAcademyManagementProps {
    setGameState: SetStateAction<any>;
}

export const useYouthAcademyManagement = ({ setGameState }: UseYouthAcademyManagementProps) => {
    const upgradeYouthAcademy = useCallback((): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }
                
                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const playerTeam = getPlayerTeam(newState);
                const currentLevel = playerTeam.youthAcademy.level;
                
                if (currentLevel >= YOUTH_ACADEMY_LEVELS.length) {
                    resolve({ success: false, message: "Academy is already at maximum level." });
                    return prevState;
                }
                
                const upgradeInfo = YOUTH_ACADEMY_LEVELS[currentLevel];
                if (playerTeam.finances.balance < upgradeInfo.upgradeCost) {
                    resolve({ success: false, message: "Not enough funds to upgrade academy." });
                    return prevState;
                }

                playerTeam.youthAcademy.level++;
                playerTeam.finances.balance -= upgradeInfo.upgradeCost;
                playerTeam.finances.history.push({ season: newState.season, week: newState.currentWeek, event: `Upgraded Youth Academy to Level ${playerTeam.youthAcademy.level}`, amount: -upgradeInfo.upgradeCost });
                
                resolve({ success: true, message: `Youth academy upgraded to Level ${playerTeam.youthAcademy.level}!`});
                return newState;
            });
        });
    }, [setGameState]);

    const downgradeYouthAcademy = useCallback((): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }
                
                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const playerTeam = getPlayerTeam(newState);
                const currentLevel = playerTeam.youthAcademy.level;

                if (currentLevel <= 1) {
                    resolve({ success: false, message: "Academy is already at the minimum level." });
                    return prevState;
                }

                playerTeam.youthAcademy.level--;
                addNewsItem(newState, 'Academy Downgraded', `The board has downgraded the youth academy to Level ${playerTeam.youthAcademy.level} to reduce staff wage costs.`, 'finance');
                
                resolve({ success: true, message: `Youth academy downgraded to Level ${playerTeam.youthAcademy.level}.`});
                return newState;
            });
        });
    }, [setGameState]);

    const promoteYouthPlayer = useCallback((player: Player): Promise<{ success: boolean, message: string }> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve({ success: false, message: "Game not started." });
                    return prevState;
                }
            
                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const playerTeam = getPlayerTeam(newState);
            
                if (playerTeam.squad.length >= MAX_SQUAD_SIZE) {
                    resolve({ success: false, message: "Senior squad is full. Sell or dismiss a player to make room." });
                    return prevState;
                }
            
                const playerIndexInYouth = playerTeam.youthAcademy.squad.findIndex(p => p.id === player.id);
                if (playerIndexInYouth === -1) {
                    resolve({ success: false, message: "Player not found in youth academy." });
                    return prevState;
                }
                
                const promotedPlayer = playerTeam.youthAcademy.squad.splice(playerIndexInYouth, 1)[0];
                promotedPlayer.isPromotedYouth = true;
                promotedPlayer.promotionDate = { season: newState.season, week: newState.currentWeek };
                promotedPlayer.wage = promotedPlayer.expectedWage || getPlayerWeeklyWage(promotedPlayer, newState.playerLeagueName, newState.gameType, newState.config);

                const contractYears = 3;
                promotedPlayer.contract = { yearsRemaining: contractYears, expirySeason: newState.season + contractYears };
                playerTeam.squad.push(promotedPlayer);
                newState.transferHistory.push({
                    season: newState.season, week: newState.currentWeek, playerName: promotedPlayer.name, playerSkill: promotedPlayer.skill, fromTeam: 'Youth Academy', toTeam: playerTeam.name, fee: 0,
                    playerId: promotedPlayer.id, playerAge: promotedPlayer.age, playerPosition: promotedPlayer.position, playerPotential: promotedPlayer.potential
                });
                addNewsItem(newState, 'Youth Promotion', `${promotedPlayer.name} has been promoted from the youth academy and signed a ${contractYears}-year contract.`, 'youth', undefined, { type: 'playerDetail', title: `Player: ${promotedPlayer.name}`, playerId: promotedPlayer.id });
                
                resolve({ success: true, message: `${player.name} has been promoted to the senior squad!` });
                return newState;
            });
        });
    }, [setGameState]);

    const updateYouthPreferences = useCallback((preferences: { roleFocus?: string; footPreference?: string; widthFocus?: string; playingStyle?: string; }): void => {
        setGameState(prevState => {
            if (!prevState.gameStarted) {
                return prevState;
            }

            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            const playerTeam = getPlayerTeam(newState);

            // Update preferences with current week
            playerTeam.youthAcademy.preferences = {
                roleFocus: (preferences.roleFocus as any) || 'Balanced',
                footPreference: (preferences.footPreference as any) || 'Balanced',
                widthFocus: (preferences.widthFocus as any) || 'Balanced',
                playingStyle: (preferences.playingStyle as any) || 'Balanced',
                preferencesSetWeek: newState.currentWeek,
            };

            return newState;
        });
    }, [setGameState]);

    return {
        upgradeYouthAcademy,
        downgradeYouthAcademy,
        promoteYouthPlayer,
        updateYouthPreferences,
    };
};